/**
 * Sincroniza fibras del cable al guardar config POP/OLT (marca olt / libera).
 * @module utils/syncOltFibersToCables
 */

import type { Cable, Fibra } from '@/types/fibers'
import {
  collectOltCableFibraBindingsFromConfig,
  isOltConectadaAForNode,
  oltConectadaARef,
  type OltFibraBinding,
} from './oltCableBindings'

const OLT_ESTADO = 'olt' as const

function pairKey(cableId: string, fibraId: string) {
  return `${cableId}::${fibraId}`
}

function mapBindings(bindings: OltFibraBinding[]) {
  const m = new Map<string, OltFibraBinding>()
  for (const b of bindings) m.set(pairKey(b.cableId, b.fibraId), b)
  return m
}

function updateFibraById(
  cable: Cable,
  fibraId: string,
  updater: (f: Fibra) => Fibra
): { next: Cable; changed: boolean } {
  if (cable.tieneBuffers && cable.buffers && cable.buffers.length > 0) {
    let changed = false
    const buffers = cable.buffers.map((buf) => ({
      ...buf,
      fibras: buf.fibras.map((f) => {
        if (f.fibraId !== fibraId) return f
        const prev = f as Fibra
        const nf = updater(prev)
        if (nf.estado !== prev.estado || nf.conectadaA !== prev.conectadaA) changed = true
        return nf
      }),
    }))
    if (changed) return { next: { ...cable, buffers }, changed: true }
  }

  if (cable.fibras && cable.fibras.length > 0) {
    let changed = false
    const fibras = cable.fibras.map((f) => {
      if (f.fibraId !== fibraId) return f
      const prev = f as Fibra
      const nf = updater(prev)
      if (nf.estado !== prev.estado || nf.conectadaA !== prev.conectadaA) changed = true
      return nf
    })
    if (changed) return { next: { ...cable, fibras }, changed: true }
  }

  return { next: cable, changed: false }
}

/**
 * Aplica cambios de ocupación OLT sobre los cables afectados.
 * Solo modifica fibras con estado `olt` o conectadaA generada por este nodo.
 */
export async function syncOltFibersAfterNodeConfigSave(params: {
  nodeId: string
  previousConfig: unknown
  nextConfig: unknown
  cables: Cable[]
  updateCable: (cable: Cable) => Promise<void>
}): Promise<void> {
  const { nodeId, previousConfig, nextConfig, cables, updateCable } = params

  const prev = collectOltCableFibraBindingsFromConfig(previousConfig, nodeId, '')
  const next = collectOltCableFibraBindingsFromConfig(nextConfig, nodeId, '')
  const prevMap = mapBindings(prev)
  const nextMap = mapBindings(next)

  const cableIds = new Set<string>()
  for (const k of prevMap.keys()) cableIds.add(k.split('::')[0])
  for (const k of nextMap.keys()) cableIds.add(k.split('::')[0])

  for (const cableId of cableIds) {
    const cable = cables.find((c) => c.id === cableId)
    if (!cable) continue

    let working = cable

    for (const [key, binding] of prevMap) {
      if (!key.startsWith(`${cableId}::`)) continue
      if (nextMap.has(key)) continue
      const fibraId = binding.fibraId
      const { next: n, changed } = updateFibraById(working, fibraId, (f) => {
        const ours =
          f.estado === OLT_ESTADO || isOltConectadaAForNode(f.conectadaA, nodeId)
        if (!ours) return f
        return {
          ...f,
          estado: 'libre',
          conectadaA: null,
        } as Fibra
      })
      if (changed) working = n
    }

    for (const [key, binding] of nextMap) {
      if (!key.startsWith(`${cableId}::`)) continue
      const ref = oltConectadaARef(binding)
      const { next: n, changed } = updateFibraById(working, binding.fibraId, (f) => ({
        ...f,
        estado: OLT_ESTADO as Fibra['estado'],
        conectadaA: ref,
      }))
      if (changed) working = n
    }

    if (working !== cable) {
      await updateCable(working)
    }
  }
}
